// Applicant portal — application status + documents. Row-scoped server-side to
// the caller's own job application (portal.read_status / upload_documents).
// Surfaces a status timeline derived from the ApplicationStatus enum, an
// application summary, the uploaded-document list, and a base64 upload control
// (10 MB client cap, accepts PDF / images).

import { useRef, useState } from 'react';
import type { ApplicationStatus, JobApplication } from '@dsr/shared-types';
import { useMyApplication, useUploadApplicationDocument } from '../api/queries/portal';
import { ApiError } from '../api/client';
import { toast } from '../components/Toasts';
import { Table } from '../components/ui';
import { fmtDate, fmtDateTime } from '../utils/date';

// Forward pipeline order. `hired` / `rejected` are terminal — a rejected
// application short-circuits the timeline at the stage it reached.
const PIPELINE: { value: ApplicationStatus; label: string }[] = [
  { value: 'new', label: 'Submitted' },
  { value: 'screening', label: 'Screening' },
  { value: 'interview', label: 'Interview' },
  { value: 'offer', label: 'Offer' },
  { value: 'hired', label: 'Hired' },
];

const STATUS_LABEL: Record<ApplicationStatus, string> = {
  new: 'Submitted',
  screening: 'Screening',
  interview: 'Interview',
  offer: 'Offer',
  hired: 'Hired',
  rejected: 'Not selected',
};

const MAX_BYTES = 10 * 1024 * 1024; // 10 MB client cap
const ACCEPT = 'application/pdf,image/*';

function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read the file.'));
    reader.onload = () => {
      // result is a data: URL; strip the "data:...;base64," prefix.
      const result = typeof reader.result === 'string' ? reader.result : '';
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(file);
  });
}

// ---------- Status timeline ----------

function StatusTimeline({ status }: { status: ApplicationStatus }): JSX.Element {
  const rejected = status === 'rejected';
  // Index of the current stage along the forward pipeline. A rejected
  // application never reached 'hired', so clamp it below the final stage.
  const currentIndex = rejected
    ? PIPELINE.length - 2
    : PIPELINE.findIndex((s) => s.value === status);

  return (
    <ol className="rpt-timeline portal-status-timeline">
      {PIPELINE.map((step, i) => {
        const done = currentIndex >= 0 && i < currentIndex;
        const active = i === currentIndex && !rejected;
        const tone = active ? 'badge-active' : done ? 'badge-new' : '';
        return (
          <li key={step.value} className="rpt-timeline-item">
            <div className="rpt-timeline-desc">
              <span className={`badge ${tone}`}>{step.label}</span>
              {active && <span className="muted small"> — current stage</span>}
            </div>
          </li>
        );
      })}
      {rejected && (
        <li className="rpt-timeline-item">
          <div className="rpt-timeline-desc">
            <span className="badge badge-danger">Not selected</span>
          </div>
        </li>
      )}
    </ol>
  );
}

// ---------- Application summary ----------

function Summary({ app }: { app: JobApplication }): JSX.Element {
  return (
    <section className="detail-card">
      <h2>Application</h2>
      <dl className="detail-grid">
        <div>
          <dt>Name</dt>
          <dd>{app.full_name}</dd>
        </div>
        <div>
          <dt>Position</dt>
          <dd>{app.position ?? '—'}</dd>
        </div>
        <div>
          <dt>Email</dt>
          <dd>{app.email ?? '—'}</dd>
        </div>
        <div>
          <dt>Phone</dt>
          <dd>{app.phone ?? '—'}</dd>
        </div>
        <div>
          <dt>Status</dt>
          <dd>{STATUS_LABEL[app.status]}</dd>
        </div>
        <div>
          <dt>Submitted</dt>
          <dd>{fmtDate(app.created_at)}</dd>
        </div>
        {app.cover_note && (
          <div className="detail-grid-full">
            <dt>Cover note</dt>
            <dd>{app.cover_note}</dd>
          </div>
        )}
      </dl>
    </section>
  );
}

// ===========================================================================
// Page
// ===========================================================================

export function PortalApplicationPage(): JSX.Element {
  const { data, isPending, isError, error } = useMyApplication();
  const upload = useUploadApplicationDocument();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const onPickFile = async (file: File | undefined): Promise<void> => {
    setUploadError(null);
    if (!file) return;
    if (file.size > MAX_BYTES) {
      setUploadError('That file is over the 10 MB limit.');
      return;
    }
    try {
      const content_base64 = await readAsBase64(file);
      await upload.mutateAsync({ file_name: file.name, content_base64 });
      toast.success('Document uploaded.');
      if (fileRef.current) fileRef.current.value = '';
    } catch (err) {
      setUploadError(err instanceof ApiError ? err.message : 'Could not upload the document.');
    }
  };

  if (isPending) {
    return <div className="page-state">Loading your application…</div>;
  }
  if (isError || !data) {
    return (
      <div className="page-state page-error" role="alert">
        {error instanceof ApiError ? error.message : 'Failed to load your application.'}
      </div>
    );
  }

  const { application, documents } = data;

  return (
    <div className="page portal-application-page">
      <header className="page-header">
        <div>
          <h1>My application</h1>
          <p className="page-sub">Track your application status and upload supporting documents.</p>
        </div>
      </header>

      <section className="detail-card">
        <h2>Status</h2>
        <StatusTimeline status={application.status} />
      </section>

      <Summary app={application} />

      <section className="detail-card">
        <div className="section-header">
          <h2>Documents</h2>
          <label className="btn btn-primary btn-sm">
            {upload.isPending ? 'Uploading…' : 'Upload document'}
            <input
              ref={fileRef}
              type="file"
              accept={ACCEPT}
              disabled={upload.isPending}
              style={{ display: 'none' }}
              onChange={(e) => void onPickFile(e.target.files?.[0])}
            />
          </label>
        </div>
        <p className="muted small">PDF or image files up to 10 MB.</p>
        {uploadError && (
          <p className="form-error error-text" role="alert">
            {uploadError}
          </p>
        )}
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>File</Table.HeaderCell>
              <Table.HeaderCell>Uploaded</Table.HeaderCell>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {documents.length === 0 && (
              <Table.Empty colSpan={2}>No documents uploaded yet.</Table.Empty>
            )}
            {documents.map((d) => (
              <Table.Row key={d.id}>
                <Table.Cell tone="strong">{d.file_name}</Table.Cell>
                <Table.Cell tone="muted">{fmtDateTime(d.uploaded_at)}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </section>
    </div>
  );
}
